import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const LANG_KEY = "bhpm_lang";

// Full copy dictionary. Every user-visible string lives here.
const DICT = {
  en: {
    // Header / Nav
    "nav.why": "Why PropHero",
    "nav.services": "Services",
    "nav.technology": "Technology",
    "nav.process": "Process",
    "cta.schedule": "Schedule a Consultation",
    "cta.menu": "Menu",

    // Hero
    "hero.line1": "Proactive",
    "hero.line2": "property management",
    "hero.subhead":
      "PropHero partners directly with condominium associations across South Florida — replacing slow national firms with proactive stewardship, full transparency, and a simple owner portal backed by a CAM-licensed team.",
    "hero.cta": "Get a Custom Proposal",
    "hero.location": "North Miami Beach, Florida",
    "hero.imgAlt": "Modern South Florida high-rise",

    // Stats
    "stats.doors": "Doors Under Management",
    "stats.units": "Units Across Communities",
    "stats.utility": "Avg. Utility Cost Reduction",
    "stats.years": "Years in Business",
    "stats.yearsSuffix": " yrs",
    "stats.support": "Resident & Board Support",

    // Services
    "services.h1a": "A complete ",
    "services.h1b": "operating system",
    "services.h1c": " for garden style, mid- and high-rise communities.",
    "services.lede":
      "Every service is delivered in-house — coordinated by the same team, accountable to the same standard.",
    "services.fin.title": "Financial & Accounting",
    "services.fin.body":
      "A/R & A/P, lockbox, ACH/card payments, GAAP statements, bank recs, reserve-study funding, investment reviews.",
    "services.admin.title": "Administrative Support",
    "services.admin.body":
      "Meeting notices, agendas, minutes, e-voting, violation tracking, governance amendments, DBPR filings.",
    "services.board.title": "Board Communication",
    "services.board.body":
      "24/7 communication with the board for live, transparent decision-making.",
    "services.maint.title": "Maintenance & Capital Projects",
    "services.maint.body":
      "Site walks, vendor vetting, project oversight (façade, roof, HVAC, elevators), preventive maintenance.",
    "services.res.title": "Resident & Lifestyle",
    "services.res.body":
      "Tech-focused 24/7 portal & mobile app, digital payments, amenity bookings, bilingual communications.",
    "services.emer.title": "Emergency & Compliance",
    "services.emer.body":
      "Hurricane plans, generator testing, FLS drills, records retention, disaster recovery coordination.",
    "services.vendor.title": "Vendor & Contract Admin",
    "services.vendor.body":
      "Price benchmarking, COI tracking, performance scorecards, SLA enforcement.",
    "services.gov.title": "Governance & Collections",
    "services.gov.body":
      "Delinquency letters, payment plans, attorney interface, election supervision, statutory filings.",

    // Technology
    "tech.h1a": "Community ",
    "tech.h1b": "portal",
    "tech.lede":
      "We replaced the binders, the voicemails, and the quarterly surprises with software that works the way modern communities expect — instant, transparent, accountable.",
    "tech.resident.title": "Resident Portal",
    "tech.resident.body":
      "Payments, amenity bookings, work orders, bilingual alerts — 24/7.",
    "tech.comm.title": "24/7 Communication",
    "tech.comm.body": "Direct, real-time channel between the board and management.",
    "tech.fin.title": "Live Financials",
    "tech.fin.body":
      "Live P&L, balance sheet, and reserve health. No more PDF chases.",
    "tech.vault.title": "Document Vault",
    "tech.vault.body":
      "Every record, contract, and report — searchable and secure.",
    "tech.caption.left": "Resident / Board OS",
    "tech.caption.right": "Desktop · Mobile",

    // Onboarding
    "onboarding.h1a": "A five-step ",
    "onboarding.h1b": "handover",
    "onboarding.h1c": " built for boards that don't have time to lose.",
    "onboarding.s1.t": "Discovery Call",
    "onboarding.s1.d":
      "30-minute conversation with a managing partner. No sales script.",
    "onboarding.s2.t": "Diagnostic",
    "onboarding.s2.d":
      "A deep dive — walk the property, map vendors, financials, and your board's pain points.",
    "onboarding.s3.t": "Transition Plan",
    "onboarding.s3.d":
      "30 / 60 / 90-day roadmap — every system, every contract, every dollar.",
    "onboarding.s4.t": "Portal Go-Live",
    "onboarding.s4.d":
      "Residents and board onboarded. Tech-enabled from day one — zero downtime for residents.",
    "onboarding.s5.t": "Operate & Optimize",
    "onboarding.s5.d":
      "Monthly financials, quarterly strategy, proactive and preventative maintenance, and 24/7 direct access to managing partners.",

    // Service Area / CAM-Licensed Trust (SEO-targeted)
    "serviceArea.tag": "CAM-Licensed · Florida",
    "serviceArea.h1a": "CAM-Licensed Management for South Florida",
    "serviceArea.h1b": "Condominium Associations",
    "serviceArea.body1":
      "PropHero provides CAM-licensed condominium association management for communities across South Florida. Our approach combines responsive board communication, organized vendor coordination, financial transparency, and proactive building oversight to help associations operate with clarity and confidence.",
    "serviceArea.body2":
      "We partner with condominium associations across South Florida to replace slow, outdated management processes with a modern, accountable approach — built around service, clarity, and trust. Every managing partner is a Florida CAM-licensed professional operating under Chapter 468 Part VIII statutes.",
    "serviceArea.areasTag": "Areas We Serve",
    "serviceArea.areasH1": "South Florida condominium and HOA management coverage.",
    "serviceArea.areasFooter":
      "We serve Miami, Miami Beach, Bay Harbor Islands, Surfside, Bal Harbour, Sunny Isles Beach, Aventura, North Miami Beach, Hallandale Beach, Hollywood, Fort Lauderdale, and surrounding South Florida communities.",

    // Contact / Calendly
    "contact.cta.h1a": "Schedule a ",
    "contact.cta.h1b": "consultation",
    "contact.cta.subtext":
      "Book a 30-minute consultation with one of our managing partners.",
    "contact.cta.button": "Schedule a Consultation",
    "contact.cta.tagline": "30 min · confidential · no sales script",
    "contact.calendly.title": "30-Minute Phone Consultation",
    "contact.calendly.thanks": "Thank you — your consultation is booked.",
    "contact.calendly.thanks.body":
      "A confirmation email is on its way. We'll call you at the number you provided at the time you selected.",
    "contact.calendly.bookAnother": "Book another time",

    // Footer
    "footer.tagline":
      "Tech-enabled property management for South Florida's garden-style, mid- and high-rise communities.",
    "footer.contact": "Contact",
    "footer.compliance": "Compliance",
    "footer.cam": "Florida CAM-licensed · Insured · Bonded",
    "footer.chapter": "Operating under Chapter 468 Part VIII statutes.",
    "footer.copyright": "© 2026 PropHero. All rights reserved.",
    "footer.privacy": "Privacy Policy",
    "footer.tag2": "Tech-Driven · CAM-Licensed",

    // Privacy Policy page
    "privacy.eyebrow": "Legal · PropHero",
    "privacy.headline": "Privacy Policy",
    "privacy.updatedLabel": "Last updated:",
    "privacy.updated": "August 8, 2026",
    "privacy.intro":
      "PropHero (\u201Cwe,\u201D \u201Cus,\u201D or \u201Cour\u201D) respects your privacy. This policy explains what we collect, how we use it, and the choices you have. It applies to ",
    "privacy.intro2": " and to consultations booked through the site.",
    "privacy.collect.h": "Information we collect",
    "privacy.collect.p1":
      "When you contact us through the website or book a consultation, we may collect:",
    "privacy.collect.li1": "Your name and title",
    "privacy.collect.li2": "Email address and phone number",
    "privacy.collect.li3":
      "Property or community details (e.g., building name, address, number of units)",
    "privacy.collect.li4": "Any message or notes you submit to us",
    "privacy.collect.li5":
      "Basic technical data (pages viewed, device type, language) via privacy-respecting analytics",
    "privacy.collect.p2":
      "Consultation booking is handled by our scheduling provider (Calendly). When you book through their widget, the information you submit is also processed by Calendly under their own privacy terms.",
    "privacy.use.h": "How we use your information",
    "privacy.use.p1": "We use the information you provide only to:",
    "privacy.use.li1": "Respond to your inquiries",
    "privacy.use.li2": "Schedule and follow up on consultations",
    "privacy.use.li3": "Deliver property management services you request",
    "privacy.use.li4":
      "Improve the content, performance, and security of our website",
    "privacy.nosell.h": "We do not sell your information",
    "privacy.nosell.p":
      "We do not sell, rent, or trade your personal information to third parties. We only share information with service providers that help us operate the site and deliver our services \u2014 for example, our scheduling provider (Calendly) and standard web hosting and analytics tools \u2014 and only to the extent necessary for them to perform their work.",
    "privacy.retention.h": "Data retention & security",
    "privacy.retention.p":
      "We retain contact information for as long as reasonably necessary to respond to your request, deliver services, and meet our legal and business obligations. We use reasonable administrative and technical safeguards to protect your information, although no system is 100% secure.",
    "privacy.choices.h": "Your choices",
    "privacy.choices.p1":
      "You can ask us to access, correct, or delete the personal information we have about you at any time by emailing ",
    "privacy.choices.p2":
      ". You can also decline optional cookies or tracking through your browser settings.",
    "privacy.updates.h": "Updates to this policy",
    "privacy.updates.p":
      "We may update this policy from time to time. The \u201CLast updated\u201D date above reflects the most recent revision. Material changes will be reflected on this page.",
    "privacy.contact.h": "Contact us",
    "privacy.contact.p":
      "If you have questions about this policy or your information:",
    "privacy.back": "Back",
    "privacy.backHome": "Back to home",
    "privacy.crumb.home": "Home",
    "privacy.crumb.current": "Privacy Policy",
    "privacy.copyright": "Copyright © 2026 PropHero LLC. All rights reserved.",

    // ================== PreviewMock (landing) ==================
    // Header
    "pm.nav.services": "Services",
    "pm.nav.technology": "Technology",
    "pm.nav.compare": "Compare",
    "pm.nav.process": "Process",
    "pm.cta.schedule": "Schedule",
    // Hero
    "pm.hero.h1a": "Proactive property",
    "pm.hero.h1b": "management,",
    "pm.hero.h1c": "built for boards.",
    "pm.hero.sub": "CAM-licensed management for South Florida condos and HOAs. Transparent financials, 24/7 communication, and a portal boards actually use.",
    "pm.hero.cta": "Schedule a Consultation",
    "pm.hero.badge.cam": "CAM-Licensed",
    "pm.hero.badge.fid": "Fiduciary",
    "pm.hero.card.reserve": "Reserve Fund",
    "pm.hero.card.live": "Live",
    "pm.hero.card.target": "72% of 2026 target",
    // Proof strip
    "pm.stat.doors.n": "450+",
    "pm.stat.doors.l": "Doors under management",
    "pm.stat.cut.n": "18%",
    "pm.stat.cut.l": "Avg. operating cost cut",
    "pm.stat.years.n": "8",
    "pm.stat.years.l": "Years in business",
    "pm.stat.support.n": "24/7",
    "pm.stat.support.l": "Resident & Board support",
    // Services
    "pm.svc.h1a": "Every service your board needs.",
    "pm.svc.h1b": "Nothing they don’t.",
    "pm.svc.showLess": "Show less",
    "pm.svc.viewAll": "View all services",
    "pm.svc.fin.t": "Financial & Accounting",
    "pm.svc.fin.d": "Monthly P&Ls, AP/AR, reserve studies, tax filing — full transparency, always.",
    "pm.svc.vendor.t": "Vendor Management",
    "pm.svc.vendor.d": "3-bid rule on every project. Vetted, insured, and coordinated end-to-end.",
    "pm.svc.maint.t": "Maintenance Oversight",
    "pm.svc.maint.d": "Preventive schedules, work orders, compliance inspections — nothing slips.",
    "pm.svc.comm.t": "24/7 Communication",
    "pm.svc.comm.d": "Real humans on-call for residents. Instant board escalations when it counts.",
    "pm.svc.admin.t": "Administrative Support",
    "pm.svc.admin.d": "Meeting prep, minutes, records, and document retention — handled end-to-end.",
    "pm.svc.board.t": "Board Communication",
    "pm.svc.board.d": "Clear weekly updates, live board dashboards, and open-book decision making.",
    "pm.svc.res.t": "Resident & Lifestyle",
    "pm.svc.res.d": "Amenity coordination, moves, deliveries, and community events that actually happen.",
    "pm.svc.gov.t": "Governance & Collections",
    "pm.svc.gov.d": "Rule enforcement, delinquency management, and CAM-compliant procedures.",
    // Technology
    "pm.tech.h1a": "One portal.",
    "pm.tech.h1b": "Everyone aligned.",
    "pm.tech.sub": "Board members see live financials. Owners pay in two taps. Vendors submit bids where they belong. No PDFs. No email chains.",
    "pm.tech.pill.fin": "Real-time financials",
    "pm.tech.pill.approvals": "Board approvals",
    "pm.tech.pill.bids": "Vendor bids",
    "pm.tech.pill.payments": "Owner payments",
    "pm.tech.pill.orders": "Work orders",
    "pm.tech.pill.reserve": "Reserve tracking",
    "pm.tech.cap.fin": "Live P&Ls, AP/AR, and reserve totals update the moment a transaction posts. No month-end waiting.",
    "pm.tech.cap.approvals": "Approve invoices, contracts, and vendor bids in one click — mobile or desktop.",
    "pm.tech.cap.bids": "Competitive vendor bidding. Every proposal side-by-side, with insurance and licenses verified.",
    "pm.tech.cap.payments": "Owners pay dues in two taps. Auto-pay, statements, and payment history — no paper.",
    "pm.tech.cap.orders": "Residents open tickets from their phone. Assigned, dispatched, and closed with photos.",
    "pm.tech.cap.reserve": "Reserve balance, target, and contribution schedule — always visible to the board.",
    // Comparison
    "pm.cmp.h1": "PropHero vs. the rest.",
    "pm.cmp.col.cap": "Capability",
    "pm.cmp.col.ph": "PropHero",
    "pm.cmp.col.nat": "National firm",
    "pm.cmp.row.access": "Direct access to a managing partner",
    "pm.cmp.row.bids": "Competitive vendor bidding",
    "pm.cmp.row.fin": "Monthly transparent financials",
    "pm.cmp.row.comm": "24/7 communication",
    "pm.cmp.row.portal": "Modern community portal",
    "pm.cmp.row.fees": "Hidden vendor referral fees",
    // Process (5-step)
    "pm.proc.h1a": "A five-step process.",
    "pm.proc.h1b": "Predictable from day one.",
    "pm.proc.s1.t": "Discovery call",
    "pm.proc.s1.chip": "Discover",
    "pm.proc.s1.d": "30 min with a partner. No sales script.",
    "pm.proc.s2.t": "Diagnostic",
    "pm.proc.s2.chip": "Diagnose",
    "pm.proc.s2.d": "Walk the property, map vendors, financials, and pain points.",
    "pm.proc.s3.t": "Transition plan",
    "pm.proc.s3.chip": "Plan",
    "pm.proc.s3.d": "30/60/90-day roadmap — every system, every contract.",
    "pm.proc.s4.t": "Portal go-live",
    "pm.proc.s4.chip": "Launch",
    "pm.proc.s4.d": "Community onboarded. Zero downtime.",
    "pm.proc.s5.t": "Operate",
    "pm.proc.s5.chip": "Operate",
    "pm.proc.s5.d": "24/7 communication. Monthly financials. Proactive property management.",
    // Final CTA
    "pm.final.h1a": "Book a 30-minute call with a",
    "pm.final.h1b": "managing partner. No sales script.",
    "pm.final.cta": "Schedule a Consultation",
    // Footer
    "pm.foot.tagline": "Tech-enabled property management for South Florida's garden-style, mid- and high-rise communities.",
    "pm.foot.contact": "Contact",
    "pm.foot.compliance": "Compliance",
    "pm.foot.city": "Miami, Florida",
    "pm.foot.camLine1": "Florida CAM-licensed · Insured · Bonded",
    "pm.foot.camLine2": "Operating under Chapter 468 Part VIII statutes.",
    "pm.foot.copyright": "Copyright © 2026 PropHero LLC. All rights reserved.",
    "pm.foot.privacy": "Privacy Policy",
    "pm.foot.tagTech": "Tech-Driven",
    "pm.foot.tagCam": "CAM-Licensed",
    "pm.foot.tagRight": "Tech-Driven · CAM-Licensed",
    "pm.foot.location": "United States",

    // Lead-note form
    "leadNote.tab.schedule": "Schedule a call",
    "leadNote.tab.note": "Leave a note",
    "leadNote.title": "Not ready to schedule a call?",
    "leadNote.subtitle": "Leave us a note and our team will get back to you.",
    "leadNote.field.name": "Name",
    "leadNote.field.email": "Email",
    "leadNote.field.phone": "Phone",
    "leadNote.field.community": "Community / Association Name",
    "leadNote.field.message": "Message",
    "leadNote.field.optional": "optional",
    "leadNote.submit.send": "Send Message",
    "leadNote.submit.sending": "Sending…",
    "leadNote.success.title": "Message sent",
    "leadNote.success.body": "Thanks — our team will get back to you shortly.",
    "leadNote.err.nameRequired": "Please enter your name.",
    "leadNote.err.emailInvalid": "Please enter a valid email.",
    "leadNote.err.messageShort": "Please write at least a few words.",
    "leadNote.err.rateLimit": "Too many submissions. Please try again in a minute.",
    "leadNote.err.generic": "Something went wrong. Please try again.",
    "leadNote.err.network": "Network issue — please check your connection and try again.",
  },
  es: {
    // Header / Nav
    "nav.why": "Por qué PropHero",
    "nav.services": "Servicios",
    "nav.technology": "Tecnología",
    "nav.process": "Proceso",
    "cta.schedule": "Agendar una Consulta",
    "cta.menu": "Menú",

    // Hero
    "hero.line1": "Proactiva",
    "hero.line2": "administración de propiedades",
    "hero.subhead":
      "PropHero colabora directamente con asociaciones de condominios en todo el sur de Florida — reemplazando a firmas nacionales lentas con supervisión proactiva, transparencia total y un portal de propietarios simple respaldado por un equipo con licencia CAM.",
    "hero.cta": "Obtener una Propuesta",
    "hero.location": "North Miami Beach, Florida",
    "hero.imgAlt": "Torre moderna del sur de Florida",

    // Stats
    "stats.doors": "Unidades Administradas",
    "stats.units": "Unidades en Comunidades",
    "stats.utility": "Reducción Promedio de Servicios",
    "stats.years": "Años en el Negocio",
    "stats.yearsSuffix": " años",
    "stats.support": "Soporte a Residentes y Junta",

    // Services
    "services.h1a": "Un ",
    "services.h1b": "sistema operativo",
    "services.h1c": " completo para comunidades tipo jardín, mid-rise y high-rise.",
    "services.lede":
      "Cada servicio se presta internamente — coordinado por el mismo equipo, respondiendo al mismo estándar.",
    "services.fin.title": "Finanzas y Contabilidad",
    "services.fin.body":
      "Cuentas por cobrar/pagar, lockbox, pagos ACH/tarjeta, estados GAAP, conciliaciones bancarias, fondeo de reservas, revisión de inversiones.",
    "services.admin.title": "Apoyo Administrativo",
    "services.admin.body":
      "Convocatorias, agendas, actas, votación electrónica, seguimiento de violaciones, enmiendas de gobierno, trámites DBPR.",
    "services.board.title": "Comunicación con la Junta",
    "services.board.body":
      "Comunicación 24/7 con la junta para decisiones en vivo y transparentes.",
    "services.maint.title": "Mantenimiento y Proyectos de Capital",
    "services.maint.body":
      "Recorridos del inmueble, selección de proveedores, supervisión de proyectos (fachada, techo, HVAC, ascensores), mantenimiento preventivo.",
    "services.res.title": "Residentes y Estilo de Vida",
    "services.res.body":
      "Portal y app móvil 24/7 enfocados en tecnología, pagos digitales, reserva de amenidades, comunicaciones bilingües.",
    "services.emer.title": "Emergencias y Cumplimiento",
    "services.emer.body":
      "Planes de huracanes, pruebas de generadores, simulacros FLS, retención de registros, coordinación de recuperación ante desastres.",
    "services.vendor.title": "Contratos y Proveedores",
    "services.vendor.body":
      "Benchmark de precios, seguimiento de COI, scorecards de desempeño, cumplimiento de SLAs.",
    "services.gov.title": "Gobierno y Cobranza",
    "services.gov.body":
      "Cartas de morosidad, planes de pago, interfaz con abogados, supervisión de elecciones, presentaciones estatutarias.",

    // Technology
    "tech.h1a": "Portal ",
    "tech.h1b": "comunitario",
    "tech.lede":
      "Reemplazamos las carpetas, los mensajes de voz y las sorpresas trimestrales con software que funciona como las comunidades modernas esperan — instantáneo, transparente, responsable.",
    "tech.resident.title": "Portal del Residente",
    "tech.resident.body":
      "Pagos, reservas de amenidades, órdenes de trabajo, alertas bilingües — 24/7.",
    "tech.comm.title": "Comunicación 24/7",
    "tech.comm.body":
      "Canal directo y en tiempo real entre la junta y la administración.",
    "tech.fin.title": "Finanzas en Vivo",
    "tech.fin.body":
      "P&L, balance general y salud de reservas en vivo. Se acabó la búsqueda de PDFs.",
    "tech.vault.title": "Bóveda de Documentos",
    "tech.vault.body":
      "Cada registro, contrato y reporte — buscable y seguro.",
    "tech.caption.left": "Residente / Junta OS",
    "tech.caption.right": "Escritorio · Móvil",

    // Onboarding
    "onboarding.h1a": "Una transición de ",
    "onboarding.h1b": "cinco pasos",
    "onboarding.h1c": " diseñada para juntas que no tienen tiempo que perder.",
    "onboarding.s1.t": "Llamada de Descubrimiento",
    "onboarding.s1.d":
      "Conversación de 30 minutos con un socio administrador. Sin libreto de ventas.",
    "onboarding.s2.t": "Diagnóstico",
    "onboarding.s2.d":
      "Una inmersión profunda — recorrer la propiedad, mapear proveedores, finanzas y los puntos de dolor de su junta.",
    "onboarding.s3.t": "Plan de Transición",
    "onboarding.s3.d":
      "Hoja de ruta de 30 / 60 / 90 días — cada sistema, cada contrato, cada dólar.",
    "onboarding.s4.t": "Lanzamiento del Portal",
    "onboarding.s4.d":
      "Residentes y junta incorporados. Con tecnología desde el día uno — cero tiempo de inactividad para los residentes.",
    "onboarding.s5.t": "Operar y Optimizar",
    "onboarding.s5.d":
      "Finanzas mensuales, estrategia trimestral, mantenimiento proactivo y preventivo, y acceso directo 24/7 a los socios administradores.",

    // Service Area / CAM-Licensed
    "serviceArea.tag": "CAM-Licensed · Florida",
    "serviceArea.h1a": "Administración CAM-Licensed para asociaciones",
    "serviceArea.h1b": "de condominios del sur de Florida",
    "serviceArea.body1":
      "PropHero brinda administración CAM-Licensed de asociaciones de condominios en todo el sur de Florida. Nuestro enfoque combina comunicación receptiva con la junta, coordinación organizada de proveedores, transparencia financiera y supervisión proactiva del edificio para que las asociaciones operen con claridad y confianza.",
    "serviceArea.body2":
      "Colaboramos con asociaciones de condominios en todo el sur de Florida para reemplazar procesos de administración lentos y obsoletos con un enfoque moderno y responsable — basado en servicio, claridad y confianza. Cada socio administrador es un profesional con licencia CAM de Florida que opera bajo el Capítulo 468 Parte VIII.",
    "serviceArea.areasTag": "Áreas que Atendemos",
    "serviceArea.areasH1":
      "Cobertura de administración de condominios y HOA en el sur de Florida.",
    "serviceArea.areasFooter":
      "Atendemos Miami, Miami Beach, Bay Harbor Islands, Surfside, Bal Harbour, Sunny Isles Beach, Aventura, North Miami Beach, Hallandale Beach, Hollywood, Fort Lauderdale y comunidades aledañas del sur de Florida.",

    // Contact / Calendly
    "contact.cta.h1a": "Agendar una ",
    "contact.cta.h1b": "consulta",
    "contact.cta.subtext":
      "Reserve una consulta de 30 minutos con uno de nuestros socios administradores.",
    "contact.cta.button": "Agendar una Consulta",
    "contact.cta.tagline": "30 min · confidencial · sin libreto de ventas",
    "contact.calendly.title": "Consulta Telefónica de 30 Minutos",
    "contact.calendly.thanks": "Gracias — su consulta está reservada.",
    "contact.calendly.thanks.body":
      "Le acaba de llegar un correo de confirmación. Le llamaremos al número que proporcionó a la hora seleccionada.",
    "contact.calendly.bookAnother": "Reservar otro horario",

    // Footer
    "footer.tagline":
      "Administración con tecnología para comunidades tipo jardín, mid-rise y high-rise del sur de Florida.",
    "footer.contact": "Contacto",
    "footer.compliance": "Cumplimiento",
    "footer.cam": "Florida CAM-Licensed · Asegurada · Fianza",
    "footer.chapter": "Operando bajo Capítulo 468 Parte VIII.",
    "footer.copyright":
      "© 2026 PropHero. Todos los derechos reservados.",
    "footer.privacy": "Política de Privacidad",
    "footer.tag2": "Tecnología · CAM-Licensed",

    // Privacy Policy page
    "privacy.eyebrow": "Legal · PropHero",
    "privacy.headline": "Política de Privacidad",
    "privacy.updatedLabel": "Última actualización:",
    "privacy.updated": "28 de febrero de 2026",
    "privacy.intro":
      "PropHero (\u201Cnosotros\u201D o \u201Cnuestro\u201D) respeta su privacidad. Esta política explica qué información recopilamos, cómo la utilizamos y qué opciones tiene usted. Aplica a ",
    "privacy.intro2":
      " y a las consultas reservadas a través del sitio.",
    "privacy.collect.h": "Información que recopilamos",
    "privacy.collect.p1":
      "Cuando se comunica con nosotros a través del sitio web o reserva una consulta, podemos recopilar:",
    "privacy.collect.li1": "Su nombre y cargo",
    "privacy.collect.li2": "Correo electrónico y número de teléfono",
    "privacy.collect.li3":
      "Detalles de la propiedad o comunidad (por ejemplo, nombre del edificio, dirección, número de unidades)",
    "privacy.collect.li4": "Cualquier mensaje o nota que nos envíe",
    "privacy.collect.li5":
      "Datos técnicos básicos (páginas vistas, tipo de dispositivo, idioma) mediante analíticas que respetan la privacidad",
    "privacy.collect.p2":
      "La reserva de consultas es gestionada por nuestro proveedor de programación (Calendly). Cuando reserva a través de su widget, la información que envía también es procesada por Calendly bajo sus propios términos de privacidad.",
    "privacy.use.h": "Cómo usamos su información",
    "privacy.use.p1": "Usamos la información que nos proporciona únicamente para:",
    "privacy.use.li1": "Responder a sus consultas",
    "privacy.use.li2": "Programar y dar seguimiento a las consultas",
    "privacy.use.li3":
      "Prestar los servicios de administración de propiedades que solicite",
    "privacy.use.li4":
      "Mejorar el contenido, el rendimiento y la seguridad de nuestro sitio web",
    "privacy.nosell.h": "No vendemos su información",
    "privacy.nosell.p":
      "No vendemos, alquilamos ni comercializamos su información personal con terceros. Solo compartimos información con proveedores de servicios que nos ayudan a operar el sitio y prestar nuestros servicios \u2014 por ejemplo, nuestro proveedor de programación (Calendly) y herramientas estándar de hosting web y analíticas \u2014 y únicamente en la medida necesaria para que realicen su trabajo.",
    "privacy.retention.h": "Retención de datos y seguridad",
    "privacy.retention.p":
      "Conservamos la información de contacto durante el tiempo que sea razonablemente necesario para responder a su solicitud, prestar los servicios y cumplir con nuestras obligaciones legales y comerciales. Utilizamos salvaguardas administrativas y técnicas razonables para proteger su información, aunque ningún sistema es 100% seguro.",
    "privacy.choices.h": "Sus opciones",
    "privacy.choices.p1":
      "Puede solicitarnos acceder, corregir o eliminar la información personal que tenemos sobre usted en cualquier momento enviando un correo a ",
    "privacy.choices.p2":
      ". También puede rechazar cookies o el seguimiento opcional a través de la configuración de su navegador.",
    "privacy.updates.h": "Actualizaciones de esta política",
    "privacy.updates.p":
      "Podemos actualizar esta política ocasionalmente. La fecha de \u201CÚltima actualización\u201D indicada arriba refleja la revisión más reciente. Los cambios sustanciales se reflejarán en esta página.",
    "privacy.contact.h": "Contáctenos",
    "privacy.contact.p":
      "Si tiene preguntas sobre esta política o sobre su información:",
    "privacy.back": "Volver",
    "privacy.backHome": "Volver al inicio",
    "privacy.crumb.home": "Inicio",
    "privacy.crumb.current": "Política de privacidad",
    "privacy.copyright": "Copyright © 2026 PropHero LLC. All rights reserved.",

    // ================== PreviewMock (landing) ==================
    // Header
    "pm.nav.services": "Servicios",
    "pm.nav.technology": "Tecnología",
    "pm.nav.compare": "Comparar",
    "pm.nav.process": "Proceso",
    "pm.cta.schedule": "Agendar",
    // Hero
    "pm.hero.h1a": "Administración de",
    "pm.hero.h1b": "propiedades proactiva,",
    "pm.hero.h1c": "hecha para juntas.",
    "pm.hero.sub": "Administración con licencia CAM para condominios y HOAs del sur de Florida. Finanzas transparentes, comunicación 24/7 y un portal que las juntas realmente usan.",
    "pm.hero.cta": "Agendar una Consulta",
    "pm.hero.badge.cam": "Licencia CAM",
    "pm.hero.badge.fid": "Fiduciario",
    "pm.hero.card.reserve": "Fondo de Reserva",
    "pm.hero.card.live": "En vivo",
    "pm.hero.card.target": "72% de la meta 2026",
    // Proof strip
    "pm.stat.doors.n": "450+",
    "pm.stat.doors.l": "Unidades administradas",
    "pm.stat.cut.n": "18%",
    "pm.stat.cut.l": "Reducción prom. de costos",
    "pm.stat.years.n": "8",
    "pm.stat.years.l": "Años en el negocio",
    "pm.stat.support.n": "24/7",
    "pm.stat.support.l": "Soporte a residentes y junta",
    // Services
    "pm.svc.h1a": "Cada servicio que tu junta necesita.",
    "pm.svc.h1b": "Nada de lo que no.",
    "pm.svc.showLess": "Ver menos",
    "pm.svc.viewAll": "Ver todos los servicios",
    "pm.svc.fin.t": "Finanzas y Contabilidad",
    "pm.svc.fin.d": "P&L mensual, AP/AR, estudios de reserva, declaraciones — transparencia total, siempre.",
    "pm.svc.vendor.t": "Gestión de Proveedores",
    "pm.svc.vendor.d": "Regla de 3 cotizaciones en cada proyecto. Verificados, asegurados y coordinados de principio a fin.",
    "pm.svc.maint.t": "Supervisión de Mantenimiento",
    "pm.svc.maint.d": "Cronogramas preventivos, órdenes de trabajo, inspecciones de cumplimiento — nada se escapa.",
    "pm.svc.comm.t": "Comunicación 24/7",
    "pm.svc.comm.d": "Personas reales de guardia para los residentes. Escalamientos inmediatos a la junta cuando cuenta.",
    "pm.svc.admin.t": "Soporte Administrativo",
    "pm.svc.admin.d": "Preparación de reuniones, actas, registros y retención de documentos — manejado de principio a fin.",
    "pm.svc.board.t": "Comunicación con la Junta",
    "pm.svc.board.d": "Actualizaciones semanales claras, paneles en vivo para la junta y toma de decisiones a libro abierto.",
    "pm.svc.res.t": "Residentes y Estilo de Vida",
    "pm.svc.res.d": "Coordinación de amenidades, mudanzas, entregas y eventos comunitarios que sí suceden.",
    "pm.svc.gov.t": "Gobernanza y Cobros",
    "pm.svc.gov.d": "Aplicación de reglas, gestión de morosidad y procedimientos conformes con CAM.",
    // Technology
    "pm.tech.h1a": "Un portal.",
    "pm.tech.h1b": "Todos alineados.",
    "pm.tech.sub": "La junta ve finanzas en vivo. Los propietarios pagan en dos toques. Los proveedores presentan cotizaciones donde corresponde. Sin PDFs. Sin cadenas de correos.",
    "pm.tech.pill.fin": "Finanzas en tiempo real",
    "pm.tech.pill.approvals": "Aprobaciones de la junta",
    "pm.tech.pill.bids": "Cotizaciones de proveedores",
    "pm.tech.pill.payments": "Pagos de propietarios",
    "pm.tech.pill.orders": "Órdenes de trabajo",
    "pm.tech.pill.reserve": "Seguimiento de reservas",
    "pm.tech.cap.fin": "P&L, AP/AR y reservas se actualizan al instante en que se registra una transacción. Sin esperar al cierre de mes.",
    "pm.tech.cap.approvals": "Aprueba facturas, contratos y cotizaciones con un clic — móvil o escritorio.",
    "pm.tech.cap.bids": "Regla de 3 cotizaciones. Propuestas lado a lado, con seguros y licencias verificadas.",
    "pm.tech.cap.payments": "Los propietarios pagan en dos toques. Débito automático, estados de cuenta e historial — sin papel.",
    "pm.tech.cap.orders": "Los residentes abren tickets desde su teléfono. Asignados, despachados y cerrados con fotos.",
    "pm.tech.cap.reserve": "Saldo de reserva, meta y calendario de aportes — siempre visibles para la junta.",
    // Comparison
    "pm.cmp.h1": "PropHero vs. el resto.",
    "pm.cmp.col.cap": "Capacidad",
    "pm.cmp.col.ph": "PropHero",
    "pm.cmp.col.nat": "Firma nacional",
    "pm.cmp.row.access": "Acceso directo al socio administrador",
    "pm.cmp.row.bids": "Regla de 3 cotizaciones",
    "pm.cmp.row.fin": "Finanzas mensuales transparentes",
    "pm.cmp.row.comm": "Comunicación 24/7 con residentes",
    "pm.cmp.row.portal": "Portal comunitario moderno",
    "pm.cmp.row.fees": "Comisiones ocultas de proveedores",
    // Process
    "pm.proc.h1a": "Un proceso de cinco pasos.",
    "pm.proc.h1b": "Predecible desde el día uno.",
    "pm.proc.s1.t": "Llamada de descubrimiento",
    "pm.proc.s1.chip": "Descubrir",
    "pm.proc.s1.d": "30 min con un socio. Sin guion de ventas.",
    "pm.proc.s2.t": "Diagnóstico",
    "pm.proc.s2.chip": "Diagnóstico",
    "pm.proc.s2.d": "Recorrido de la propiedad, mapeo de proveedores, finanzas y puntos de dolor.",
    "pm.proc.s3.t": "Plan de transición",
    "pm.proc.s3.chip": "Plan",
    "pm.proc.s3.d": "Hoja de ruta a 30/60/90 días — cada sistema, cada contrato.",
    "pm.proc.s4.t": "Puesta en marcha del portal",
    "pm.proc.s4.chip": "Lanzar",
    "pm.proc.s4.d": "Comunidad incorporada. Cero tiempo fuera de servicio.",
    "pm.proc.s5.t": "Operar",
    "pm.proc.s5.chip": "Operar",
    "pm.proc.s5.d": "Comunicación 24/7. Finanzas mensuales. Administración proactiva de la propiedad.",
    // Final CTA
    "pm.final.h1a": "Reserva una llamada de 30 minutos con un",
    "pm.final.h1b": "socio administrador. Sin guion de ventas.",
    "pm.final.cta": "Agendar una Consulta",
    // Footer
    "pm.foot.tagline": "Administración de propiedades habilitada por tecnología para comunidades del sur de Florida — estilo jardín, edificios medianos y altos.",
    "pm.foot.contact": "Contacto",
    "pm.foot.compliance": "Cumplimiento",
    "pm.foot.city": "Miami, Florida",
    "pm.foot.camLine1": "Florida con licencia CAM · Asegurado · Con fianza",
    "pm.foot.camLine2": "Operando bajo el Capítulo 468 Parte VIII de los estatutos.",
    "pm.foot.copyright": "Copyright © 2026 PropHero LLC. Todos los derechos reservados.",
    "pm.foot.privacy": "Política de Privacidad",
    "pm.foot.tagTech": "Tecnológico",
    "pm.foot.tagCam": "Licencia CAM",
    "pm.foot.tagRight": "Tecnológico · Licencia CAM",
    "pm.foot.location": "Estados Unidos",

    // Lead-note form
    "leadNote.tab.schedule": "Agendar llamada",
    "leadNote.tab.note": "Dejar nota",
    "leadNote.title": "¿No listo para agendar una llamada?",
    "leadNote.subtitle": "Déjanos una nota y nuestro equipo te contactará.",
    "leadNote.field.name": "Nombre",
    "leadNote.field.email": "Correo electrónico",
    "leadNote.field.phone": "Teléfono",
    "leadNote.field.community": "Nombre de la comunidad / asociación",
    "leadNote.field.message": "Mensaje",
    "leadNote.field.optional": "opcional",
    "leadNote.submit.send": "Enviar mensaje",
    "leadNote.submit.sending": "Enviando…",
    "leadNote.success.title": "Mensaje enviado",
    "leadNote.success.body": "Gracias — nuestro equipo te contactará en breve.",
    "leadNote.err.nameRequired": "Por favor ingresa tu nombre.",
    "leadNote.err.emailInvalid": "Por favor ingresa un correo válido.",
    "leadNote.err.messageShort": "Escribe al menos unas palabras.",
    "leadNote.err.rateLimit": "Demasiados envíos. Inténtalo de nuevo en un minuto.",
    "leadNote.err.generic": "Algo salió mal. Inténtalo de nuevo.",
    "leadNote.err.network": "Problema de conexión — verifica tu red e inténtalo de nuevo.",
  },
};

const I18nContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    // 1. Highest priority: ?lang= URL param (used by hreflang link variants
    //    so a deep-link from Google's Spanish SERP lands directly in ES).
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang === "es" || urlLang === "en") return urlLang;
    } catch (e) {
      // URL parsing shouldn't fail in modern browsers, but if it does we just
      // fall through to localStorage / browser-language detection below.
      console.warn("[i18n] URLSearchParams parse failed:", e);
    }
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === "es" || saved === "en") return saved;
    // Default to Spanish if browser prefers it
    const browser = (navigator.language || "en").toLowerCase();
    return browser.startsWith("es") ? "es" : "en";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_KEY, lang);
      document.documentElement.setAttribute("lang", lang);
    } catch (e) {
      // localStorage.setItem can throw in private browsing modes or when the
      // quota is exceeded. Language persistence is best-effort — the UI still
      // works, but log so we can spot systemic issues.
      console.warn("[i18n] Failed to persist language preference:", e);
    }
  }, [lang]);

  const setLang = (l) => setLangState(l === "es" ? "es" : "en");

  const t = useMemo(() => {
    const table = DICT[lang] || DICT.en;
    return (key) => (key in table ? table[key] : DICT.en[key] || key);
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  return useContext(I18nContext);
}

export function LangToggle({ className = "" }) {
  const { lang, setLang } = useT();
  return (
    <button
      type="button"
      data-testid="lang-toggle"
      onClick={() => setLang(lang === "en" ? "es" : "en")}
      aria-label={lang === "en" ? "Switch to Spanish" : "Switch to English"}
      className={`inline-flex items-center justify-center min-w-[36px] px-2.5 py-1 rounded-full text-[11px] font-normal text-[#515154] border transition-colors hover:text-[#0A0A0A] ${className}`}
      style={{ borderColor: "#E5E5EA", letterSpacing: "0.02em" }}
    >
      {lang === "en" ? "EN" : "ES"}
    </button>
  );
}
